import { Injectable } from '@angular/core';
import { ActiveLookup, Lookups } from '@ag-common-lib/public-api';
import { combineLatest, Observable, of, shareReplay } from 'rxjs';
import { LookupsDefaultService } from 'ag-common-svc/lib/services/lookups-default.service';
import { LookupsFromDataService } from 'ag-common-svc/lib/services/lookups-from-data.service';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LookupsService {
  readonly statesLookup$: Observable<ActiveLookup[]>;
  readonly mediaTagsLookup$: Observable<ActiveLookup[]>;
  readonly gendersLookup$: Observable<ActiveLookup[]>;
  readonly dietaryConsiderationTypesLookup$: Observable<ActiveLookup[]>;
  readonly tShortSizesLookup$: Observable<ActiveLookup[]>;
  readonly tShirtAdultLookup$: Observable<ActiveLookup[]>;
  readonly tShirtYouthLookup$: Observable<ActiveLookup[]>;
  readonly shirtMenLookup$: Observable<ActiveLookup[]>;
  readonly shirtWomenLookup$: Observable<ActiveLookup[]>;
  readonly suffixesLookup$: Observable<ActiveLookup[]>;
  readonly prefixesLookup$: Observable<ActiveLookup[]>;
  readonly taskCategoryLookup$: Observable<ActiveLookup[]>;
  readonly taskSubcategoryLookup$: Observable<ActiveLookup[]>;
  readonly associationTypeLookup$: Observable<ActiveLookup[]>;
  readonly emailTypeLookup$: Observable<ActiveLookup[]>;
  readonly supportTypesLookup$: Observable<ActiveLookup[]>;
  readonly agencyStatusesLookup$: Observable<ActiveLookup[]>;
  readonly agencyOrganizationAffiliations$: Observable<ActiveLookup[]>;
  readonly agencyPhoneTypes$: Observable<ActiveLookup[]>;
  readonly agencyEmailTypes$: Observable<ActiveLookup[]>;
  readonly agencyAddressTypesLookup$: Observable<ActiveLookup[]>;
  readonly agencySocialTypesLookup$: Observable<ActiveLookup[]>;
  readonly agencySocialMediaLookup$: Observable<ActiveLookup[]>;
  readonly agencyWebsiteTypesLookup$: Observable<ActiveLookup[]>;
  readonly socialTypesLookup$: Observable<ActiveLookup[]>;
  readonly socialMediaLookup$: Observable<ActiveLookup[]>;
  readonly websiteTypesLookup$: Observable<ActiveLookup[]>;

  // Lookups from data
  readonly carriersLookup$: Observable<Partial<ActiveLookup>[]>;
  readonly mgaLookup$: Observable<Partial<ActiveLookup>[]>;
  readonly agencyLookup$: Observable<Partial<ActiveLookup>[]>;

  readonly tShirtUnisexLookup$: Observable<ActiveLookup[]>;
  readonly shirtGenderLookup$: Observable<ActiveLookup[]>;

  constructor(
    private lookupsDefaultService: LookupsDefaultService,
    private lookupsFromDataService: LookupsFromDataService,
  ) {
    this.gendersLookup$ = this.lookupsDefaultService.gendersLookup$;
    this.suffixesLookup$ = this.lookupsDefaultService.suffixesLookup$;

    this.prefixesLookup$ = this.lookupsDefaultService.prefixesLookup$;
    this.statesLookup$ = this.lookupsDefaultService.statesLookup$;
    this.mediaTagsLookup$ = this.lookupsDefaultService.mediaTagsLookup$;
    this.taskCategoryLookup$ = this.lookupsDefaultService.taskCategoryLookup$;
    this.taskSubcategoryLookup$ = this.lookupsDefaultService.taskSubcategoryLookup$;
    this.associationTypeLookup$ = this.lookupsDefaultService.associationTypeLookup$;
    this.dietaryConsiderationTypesLookup$ = this.lookupsDefaultService.dietaryConsiderationTypesLookup$;
    this.emailTypeLookup$ = this.lookupsDefaultService.emailTypeLookup$;
    this.tShortSizesLookup$ = this.lookupsDefaultService.tShortSizesLookup$;
    this.tShirtAdultLookup$ = this.lookupsDefaultService.tShirtAdultLookup$;
    this.tShirtYouthLookup$ = this.lookupsDefaultService.tShirtYouthLookup$;
    this.shirtMenLookup$ = this.lookupsDefaultService.shirtMenLookup$;
    this.shirtWomenLookup$ = this.lookupsDefaultService.shirtWomenLookup$;
    this.supportTypesLookup$ = this.lookupsDefaultService.supportTypesLookup$;
    this.agencyStatusesLookup$ = this.lookupsDefaultService.agencyStatusesLookup$;
    this.agencyOrganizationAffiliations$ = this.lookupsDefaultService.agencyOrganizationAffiliations$;
    this.agencyPhoneTypes$ = this.lookupsDefaultService.agencyPhoneTypes$;
    this.agencyEmailTypes$ = this.lookupsDefaultService.agencyEmailTypes$;
    this.agencyAddressTypesLookup$ = this.lookupsDefaultService.agencyAddressTypesLookup$;
    this.socialTypesLookup$ = this.lookupsDefaultService.socialTypesLookup$;
    this.socialMediaLookup$ = this.lookupsDefaultService.socialMediaLookup$;
    this.websiteTypesLookup$ = this.lookupsDefaultService.websiteTypesLookup$;
    this.agencySocialTypesLookup$ = this.lookupsDefaultService.agencySocialTypesLookup$;
    this.agencySocialMediaLookup$ = this.lookupsDefaultService.agencySocialMediaLookup$;
    this.agencyWebsiteTypesLookup$ = this.lookupsDefaultService.agencyWebsiteTypesLookup$;

    this.carriersLookup$ = this.lookupsFromDataService.carriersLookup$;

    this.mgaLookup$ = this.lookupsFromDataService.mgaLookup$;

    this.agencyLookup$ = this.lookupsFromDataService.agencyLookup$;

    this.tShirtUnisexLookup$ = combineLatest([this.tShirtAdultLookup$, this.tShirtYouthLookup$]).pipe(
      map(([adult, youth]) => [...adult, ...youth]),
      shareReplay(1),
    );

    this.shirtGenderLookup$ = combineLatest([this.shirtMenLookup$, this.shirtWomenLookup$]).pipe(
      map(([men, women]) => [...men, ...women]),
      shareReplay(1),
    );
  }

  public getLookupByName = (lookup: Lookups): Observable<Partial<ActiveLookup>[]> => {
    switch (lookup) {
      case Lookups.States:
        return this.statesLookup$;
      case Lookups.EmailTypes:
        return this.emailTypeLookup$;
      case Lookups.Genders:
        return this.gendersLookup$;
      case Lookups.Suffixes:
        return this.suffixesLookup$;
      case Lookups.Prefixes:
        return this.prefixesLookup$;
      case Lookups.TaskCategory:
        return this.taskCategoryLookup$;
      case Lookups.TaskSubcategory:
        return this.taskSubcategoryLookup$;
      case Lookups.AssociationType:
        return this.associationTypeLookup$;
      case Lookups.TShirtSize:
        return this.tShortSizesLookup$;
      case Lookups.TShirtAdult:
        return this.tShirtAdultLookup$;
      case Lookups.TShirtYouth:
        return this.tShirtYouthLookup$;
      case Lookups.ShirtMen:
        return this.shirtMenLookup$;
      case Lookups.ShirtWomen:
        return this.shirtWomenLookup$;
      case Lookups.DietaryConsiderationType:
        return this.dietaryConsiderationTypesLookup$;
      case Lookups.SupportTypes:
        return this.supportTypesLookup$;
      case Lookups.AgencyStatuses:
        return this.agencyStatusesLookup$;
      case Lookups.AgencyOrganizationAffiliations:
        return this.agencyOrganizationAffiliations$;
      case Lookups.AgencyPhoneTypes:
        return this.agencyPhoneTypes$;
      case Lookups.AgencyEmailTypes:
        return this.agencyEmailTypes$;
      case Lookups.SocialTypes:
        return this.socialTypesLookup$;
      case Lookups.SocialMedia:
        return this.socialMediaLookup$;
      case Lookups.WebsiteTypes:
        return this.websiteTypesLookup$;
      case Lookups.AgencySocialTypes:
        return this.agencySocialTypesLookup$;
      case Lookups.AgencySocialMedia:
        return this.agencySocialMediaLookup$;
      case Lookups.AgencyWebsiteTypes:
        return this.agencyWebsiteTypesLookup$;
      // Lookups from data
      case Lookups.Carriers:
        return this.carriersLookup$;
      case Lookups.MGA:
        return this.mgaLookup$;
      case Lookups.Agency:
        return this.agencyLookup$;
      case Lookups.MediaTags:
        return this.mediaTagsLookup$;
      default:
        return of([]);
    }
  };
}
